<template>
  <div class="searchable-select" ref="selectContainer">
    <div 
      class="select-trigger"
      @click="toggleDropdown"
      :class="{ 'is-open': isOpen, 'has-value': selectedValue }"
    >
      <span v-if="selectedValue" class="selected-value">{{ selectedLabel }}</span>
      <span v-else class="placeholder">{{ placeholder }}</span>
      <ChevronDown class="chevron" :class="{ 'is-open': isOpen }" />
    </div>

    <div v-if="isOpen" class="dropdown">
      <div class="search-box">
        <Search class="search-icon" />
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="searchPlaceholder"
          class="search-input"
          ref="searchInput"
        >
      </div>
      <div class="options-list">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="option"
          :class="{ 'is-selected': option.value === selectedValue }"
          @click="selectOption(option)"
        >
          {{ option.label }}
        </div>
        <div v-if="filteredOptions.length === 0" class="no-results">
          Aucun résultat trouvé
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ChevronDown, Search } from 'lucide-vue-next'

/* eslint-disable-next-line */
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    required: true,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Sélectionnez une option'
  },
  searchPlaceholder: {
    type: String,
    default: 'Rechercher...'
  }
})

/* eslint-disable-next-line */
const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const searchQuery = ref('')
const selectContainer = ref(null)
const searchInput = ref(null)

const selectedValue = computed(() => props.modelValue)

const selectedLabel = computed(() => {
  const selected = props.options.find(opt => opt.value === props.modelValue)
  return selected ? selected.label : ''
})

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return props.options
  }
  const query = searchQuery.value.toLowerCase()
  return props.options.filter(option =>
    option.label.toLowerCase().includes(query)
  )
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    setTimeout(() => {
      searchInput.value?.focus()
    }, 100)
  }
}

const selectOption = (option) => {
  emit('update:modelValue', option.value)
  isOpen.value = false
  searchQuery.value = ''
}

const closeDropdown = (event) => {
  if (selectContainer.value && !selectContainer.value.contains(event.target)) {
    isOpen.value = false
    searchQuery.value = ''
  }
}

onMounted(() => {
  document.addEventListener('click', closeDropdown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown)
})
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 48px;
}

.select-trigger:hover {
  border-color: #d1d5db;
}

.select-trigger.is-open {
  border-color: #d1d5db;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.select-trigger.has-value .selected-value {
  color: #021a70;
  font-weight: 500;
}

.placeholder {
  color: #9ca3af;
}

.chevron {
  transition: transform 0.3s ease;
  color: #6b7280;
}

.chevron.is-open {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-top: none;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-height: 300px;
  overflow: hidden;
}

.search-box {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.search-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: #9ca3af;
  margin-right: 0.5rem;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  color: #1f2937;
}

.search-input::placeholder {
  color: #9ca3af;
}

.options-list {
  max-height: 250px;
  overflow-y: auto;
}

.option {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: #374151;
}

.option:hover {
  background-color: #f3f4f6;
}

.option.is-selected {
  background-color: #e0f2fe;
  color: #021a70;
  font-weight: 500;
}

.no-results {
  padding: 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Custom scrollbar */
.options-list::-webkit-scrollbar {
  width: 6px;
}

.options-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.options-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.options-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
